import { ColumnDef } from "@tanstack/react-table";
import { Clock } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { SortableColumn } from "@/components/shared/sortable-column";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { mapScheduleJobToRow } from "@/components/command-team-v2/scheduled/utils/map-schedule-job";
import { cn } from "@/lib/utils";
import { formatDateTime } from "@/utils/global";

import { ScheduledJobsActionCell } from "@/components/scheduled-job/tables/scheduled-jobs-actions";

import {
  SCHEDULED_JOB_EXECUTION_STATUS_BADGE_COLORS,
  SCHEDULED_JOB_EXECUTION_STATUS_BADGE_DEFAULT,
  formatScheduledJobEntityTypeLabel,
} from "@/components/scheduled-job/constants";

function formatExecutionLabel(raw: string): string {
  return raw
    .replace(/_/g, " ")
    .toLowerCase()
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/** Matches `call-detail-card` status badge shell; colors from `constants.ts`. */
function getExecutionStatusDisplay(raw: string): {
  label: string;
  color: string;
  bgColor: string;
  borderColor: string;
} {
  const label = formatExecutionLabel(raw);
  const key = raw.trim().toLowerCase();
  if (
    key === "completed" ||
    key === "failed" ||
    key === "running" ||
    key === "due"
  ) {
    return { label, ...SCHEDULED_JOB_EXECUTION_STATUS_BADGE_COLORS[key] };
  }
  return { label, ...SCHEDULED_JOB_EXECUTION_STATUS_BADGE_DEFAULT };
}

function formatJobStatusLabel(raw: string): string {
  const s = raw.trim().toLowerCase();
  if (s === "active") return "Active";
  if (s === "expired") return "Expired";
  return raw
    .replace(/_/g, " ")
    .toLowerCase()
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function jobStatusBadgeClass(raw: string): string {
  const s = raw.trim().toLowerCase();
  const base =
    "inline-flex items-center border px-2.5 py-1 text-xs font-medium rounded-full";
  if (s === "active") {
    return `${base} bg-green-100 text-green-800 border-green-200 dark:bg-green-950/40 dark:text-green-300 dark:border-green-800`;
  }
  if (s === "expired") {
    return `${base} bg-zinc-100 text-zinc-700 border-zinc-200 dark:bg-zinc-900/50 dark:text-zinc-300 dark:border-zinc-700`;
  }
  return `${base} bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-950/40 dark:text-purple-300 dark:border-purple-800`;
}

function scheduleAgentId(job: ScheduledJob): string | null {
  const entity = (job.entity_type ?? "").toLowerCase();
  if (entity === "command_agent" && job.entity_id) return job.entity_id;
  const commandRaw = job.keyword_arguments?.command;
  if (typeof commandRaw === "string" && commandRaw.trim()) {
    try {
      const parsed = JSON.parse(commandRaw) as {
        agent_id?: string;
        dto?: { agent_id?: string };
      };
      const nested = parsed.dto?.agent_id;
      if (typeof nested === "string" && nested) return nested;
      if (typeof parsed.agent_id === "string" && parsed.agent_id) return parsed.agent_id;
    } catch {
      return job.entity_id ?? null;
    }
  }
  return job.entity_id ?? null;
}

function CadenceCell({ job }: { job: ScheduledJob }) {
  const mapped = mapScheduleJobToRow(job);
  const outlined = mapped.cadenceLabel === "once";
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium capitalize",
        outlined
          ? "border border-border/60 text-muted-foreground"
          : "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300"
      )}
    >
      <Clock className="size-3" />
      {mapped.cadenceLabel}
    </span>
  );
}

function DescriptionCell({ text }: { text: string }) {
  return (
    <div>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="block max-w-[200px] cursor-default truncate text-sm text-foreground">
            {text}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" align="start" className="max-w-sm">
          {text}
        </TooltipContent>
      </Tooltip>
    </div>
  );
}

export function getScheduledJobsColumns({
  sortBy,
  sortOrder,
  handleSort,
  stackDescription = false,
  agents = [],
}: {
  sortBy: string;
  sortOrder: string;
  handleSort: (column: string) => void;
  /** Command scheduled page: description under entity type, plus Cadence and Agent columns. */
  stackDescription?: boolean;
  agents?: Array<{ id: string; name: string }>;
}): ColumnDef<ScheduledJob>[] {
  const entityTypeColumn: ColumnDef<ScheduledJob> = {
    accessorKey: "entity_type",
    header: "Entity type",
    cell: ({ row }) => {
      const label = row.original.entity_type
        ? formatScheduledJobEntityTypeLabel(row.original.entity_type)
        : "N/A";
      if (!stackDescription) {
        return row.original.entity_type ? (
          <span className="text-sm text-foreground">{label}</span>
        ) : (
          <span className="text-gray-500">N/A</span>
        );
      }
      const description = row.original.description?.trim();
      return (
        <div className="max-w-[360px]">
          <span className="block truncate text-sm font-medium text-foreground">{label}</span>
          {description ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="block cursor-default truncate text-xs text-muted-foreground">
                  {description}
                </span>
              </TooltipTrigger>
              <TooltipContent side="top" align="start" className="max-w-sm">
                {description}
              </TooltipContent>
            </Tooltip>
          ) : (
            <span className="block truncate text-xs text-muted-foreground">—</span>
          )}
        </div>
      );
    },
  };

  const descriptionColumn: ColumnDef<ScheduledJob> = {
    accessorKey: "description",
    header: "Description",
    cell: ({ row }) => {
      const text = row.original.description?.trim();
      if (!text) {
        return (
          <div>
            <span className="text-gray-500">N/A</span>
          </div>
        );
      }
      return <DescriptionCell text={text} />;
    },
  };

  const cadenceColumn: ColumnDef<ScheduledJob> = {
    id: "cadence",
    header: "Cadence",
    cell: ({ row }) => <CadenceCell job={row.original} />,
  };

  const agentColumn: ColumnDef<ScheduledJob> = {
    id: "agent",
    header: "Agent",
    cell: ({ row }) => {
      const agentId = scheduleAgentId(row.original);
      const name = agents.find((agent) => agent.id === agentId)?.name;
      return (
        <span className="block max-w-[180px] truncate text-sm text-muted-foreground">
          {name || "—"}
        </span>
      );
    },
  };

  return [
    entityTypeColumn,
    ...(stackDescription ? [cadenceColumn, agentColumn] : [descriptionColumn]),
    {
      accessorKey: "execution_status",
      header: "Execution Status",
      cell: ({ row }) => {
        const v = row.original.execution_status;
        if (!v) {
          return <span className="text-gray-500">N/A</span>;
        }
        const { label, color, bgColor, borderColor } = getExecutionStatusDisplay(v);
        return (
          <div className="flex items-center gap-2">
            <Badge
              variant="outline"
              className="inline-flex max-w-[200px] items-center gap-1.5 truncate rounded-full px-2.5 py-1 text-xs font-medium whitespace-nowrap"
              style={{
                color,
                backgroundColor: bgColor,
                borderColor,
                borderWidth: "1px",
              }}
            >
              <span className="truncate">{label}</span>
            </Badge>
          </div>
        );
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => {
        const v = row.original.status;
        if (!v) {
          return <span className="text-gray-500">N/A</span>;
        }
        return (
          <div className="flex items-center gap-2">
            <Badge variant="outline" className={`max-w-[200px] ${jobStatusBadgeClass(v)}`}>
              <span className="truncate">{formatJobStatusLabel(v)}</span>
            </Badge>
          </div>
        );
      },
    },
    {
      accessorKey: "run_count",
      header: () => (
        <div className="flex w-full justify-center">
          <SortableColumn
            label="Runs count"
            columnKey="run_count"
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
          />
        </div>
      ),
      cell: ({ row }) => {
        const count = row.getValue("run_count") as number | undefined;
        return (
          <div className="flex w-full justify-center text-sm tabular-nums">
            {count ?? 0}
          </div>
        );
      },
    },
    {
      accessorKey: "next_run_at",
      header: () => (
        <SortableColumn
          label="Next run"
          columnKey="next_run_at"
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
        />
      ),
      cell: ({ row }) => {
        const date = row.getValue("next_run_at") as string | null | undefined;
        if (!date) {
          return <span className="text-gray-500 pl-4">N/A</span>;
        }
        const { date: dateStr, time } = formatDateTime(date);
        return (
          <div className="flex flex-col pl-4">
            <span>{dateStr}</span>
            <span className="text-muted-foreground text-sm">{time}</span>
          </div>
        );
      },
    },
    {
      accessorKey: "last_run_at",
      header: () => (
        <SortableColumn
          label="Last run"
          columnKey="last_run_at"
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
        />
      ),
      cell: ({ row }) => {
        const date = row.getValue("last_run_at") as string | null | undefined;
        if (!date) {
          return <span className="text-gray-500 pl-4">N/A</span>;
        }
        const { date: dateStr, time } = formatDateTime(date);
        return (
          <div className="flex flex-col pl-4">
            <span>{dateStr}</span>
            <span className="text-muted-foreground text-sm">{time}</span>
          </div>
        );
      },
    },
    {
      accessorKey: "created_at",
      header: () => (
        <SortableColumn
          label="Created At"
          columnKey="created_at"
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
        />
      ),
      cell: ({ row }) => {
        const date = row.getValue("created_at") as string | undefined;
        if (!date) {
          return <span className="text-gray-500 pl-4">N/A</span>;
        }
        const { date: dateStr, time } = formatDateTime(date);
        return (
          <div className="flex flex-col pl-4">
            <span>{dateStr}</span>
            <span className="text-muted-foreground text-sm">{time}</span>
          </div>
        );
      },
    },
    {
      accessorKey: "updated_at",
      header: () => (
        <SortableColumn
          label="Updated At"
          columnKey="updated_at"
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
        />
      ),
      cell: ({ row }) => {
        const date = row.getValue("updated_at") as string | undefined;
        if (!date) {
          return <span className="text-gray-500 pl-4">N/A</span>;
        }
        const { date: dateStr, time } = formatDateTime(date);
        return (
          <div className="flex flex-col pl-4">
            <span>{dateStr}</span>
            <span className="text-muted-foreground text-sm">{time}</span>
          </div>
        );
      },
    },
    {
      id: "actions",
      enableHiding: false,
      cell: ({ row }) => <ScheduledJobsActionCell row={row} />,
    },
  ];
}
